import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  UserPlus, 
  Upload, 
  Video, 
  Brain, 
  Briefcase, 
  Calendar, 
  Plane, 
  CheckCircle,
  ArrowRight,
  AlertCircle
} from 'lucide-react';

const HowItWorksCandidates = () => {
  const { t, language } = useLanguage();

  const steps = [
    {
      icon: UserPlus,
      title: t('howItWorksCandidates.step1Title'),
      description: t('howItWorksCandidates.step1Desc')
    },
    {
      icon: Upload,
      title: language === 'ar' ? 'رفع المستندات' : 'Upload Documents',
      description: language === 'ar' ? 'قدم سيرتك الذاتية وأوراق اعتمادك ورخصك وشهاداتك. نظامنا يساعد في تنظيم وهيكلة معلوماتك.' : 'Submit your CV, credentials, licences, and certifications. Our system helps organise and structure your information.'
    },
    {
      icon: Video,
      title: language === 'ar' ? 'أكمل مقابلة الفيديو' : 'Complete Video Interview',
      description: language === 'ar' ? 'سجل فيديو قصير للإجابة على أسئلة قياسية متعلقة بالدور لدعم الفحص الأولي وتقييم التواصل.' : 'Record a short video answering standard role-related questions to support initial screening and communication assessment.'
    },
    {
      icon: Brain,
      title: language === 'ar' ? 'مراجعة الملف بمساعدة الذكاء الاصطناعي' : 'AI-Assisted Profile Review',
      description: language === 'ar' ? 'يدعم نظامنا العملية من خلال هيكلة معلومات المرشح والإشارة إلى المستندات المفقودة والمساعدة في اكتمال الملف.' : 'Our system supports the process by structuring candidate information, flagging missing documentation, and assisting with profile completeness.'
    },
    {
      icon: Briefcase,
      title: language === 'ar' ? 'استلم مطابقات الوظائف' : 'Receive Job Matches',
      description: language === 'ar' ? 'احصل على مطابقة مع الوظائف التي تتوافق مع مهاراتك وخبراتك وتفضيلاتك بناءً على معلومات ملفك الشخصي.' : 'Get matched with positions that align with your skills, experience, and preferences based on your profile information.'
    },
    {
      icon: Calendar,
      title: language === 'ar' ? 'تنسيق المقابلة' : 'Interview Coordination',
      description: language === 'ar' ? 'يطلب أصحاب العمل المقابلات، وتساعد منصتنا في تنسيق الجدولة في أوقات مناسبة للطرفين.' : 'Employers request interviews, and our platform helps coordinate scheduling at mutually convenient times.'
    },
    {
      icon: CheckCircle,
      title: language === 'ar' ? 'التقدم نحو العرض' : 'Progress to Offer',
      description: language === 'ar' ? 'بعد المقابلات الناجحة، قد تتقدم إلى مرحلة العرض ومناقشات التأهيل.' : 'Following successful interviews, you may progress to offer stage and onboarding discussions.'
    },
    {
      icon: Plane,
      title: language === 'ar' ? 'إرشاد النقل' : 'Relocation Guidance',
      description: language === 'ar' ? 'نقدم التوجيه والمعلومات حول ترخيص SCFHS وعمليات التأشيرة والتحضير للنقل.' : 'We provide guidance and information on SCFHS licensing, visa processes, and relocation preparation.'
    }
  ];

  const features = [
    {
      title: language === 'ar' ? 'إرشاد ترخيص SCFHS' : 'SCFHS Licensing Guidance',
      description: language === 'ar' ? 'معلومات ودعم لتسجيل الهيئة السعودية للتخصصات الصحية وعمليات التحقق من Dataflow.' : 'Information and support for Saudi Commission for Health Specialties registration and Dataflow verification processes.'
    },
    {
      title: language === 'ar' ? 'تنظيم المستندات' : 'Document Organisation',
      description: language === 'ar' ? 'جمع مستندات منظم لدعم اكتمال أوراق الاعتماد، مع إشعارات للعناصر المفقودة أو غير المكتملة.' : 'Structured document collection to support credential completeness, with notifications for missing or incomplete items.'
    },
    {
      title: language === 'ar' ? 'معلومات النقل' : 'Relocation Information',
      description: language === 'ar' ? 'لوحة تحكم للمساعدة في تتبع حالة التأشيرة وترتيبات السفر وخطوات النقل الأخرى مع موارد التوجيه.' : 'Dashboard to help track visa status, travel arrangements, and other relocation steps with guidance resources.'
    },
    {
      title: language === 'ar' ? 'التحضير للمقابلة' : 'Interview Preparation',
      description: language === 'ar' ? 'موارد ومعلومات لمساعدتك في التحضير للمقابلات مع مرافق الرعاية الصحية السعودية.' : 'Resources and information to help you prepare for interviews with Saudi healthcare facilities.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              {t('howItWorksCandidates.title')}
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('howItWorksCandidates.subtitle')}
            </p>
          </div>

          {/* Steps */}
          <div className="mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              {language === 'ar' ? 'رحلتك في 8 خطوات' : 'Your Journey in 8 Steps'}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {steps.map((step, index) => (
                <Card key={index} className="relative hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="absolute -top-4 start-6 w-8 h-8 bg-[#42b1c2] text-white rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div className="w-12 h-12 bg-[#42b1c2]/10 rounded-lg flex items-center justify-center mb-4 mt-2">
                      <step.icon className="h-6 w-6 text-[#42b1c2]" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Video Interview Section */}
          <div className="mb-20">
            <Card className="border-2 border-purple-200 bg-purple-50/30">
              <CardContent className="p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                      <Video className="h-7 w-7 text-purple-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {language === 'ar' ? 'أكمل مقابلة الفيديو' : 'Complete Video Interview'}
                    </h3>
                    <p className="text-gray-700 mb-4">
                      {language === 'ar' ? 'سجل مقابلة فيديو قصيرة للإجابة على أسئلة قياسية متعلقة بالدور.' : 'Record a short video interview answering standard role-related questions.'}
                    </p>
                    <p className="text-gray-600 mb-4">{language === 'ar' ? 'ردودك تساعد في دعم:' : 'Your responses help support:'}</p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center space-x-2 rtl:space-x-reverse">
                        <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0" />
                        <span className="text-gray-700">{language === 'ar' ? 'الفحص الأولي' : 'Initial screening'}</span>
                      </li>
                      <li className="flex items-center space-x-2 rtl:space-x-reverse">
                        <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0" />
                        <span className="text-gray-700">{language === 'ar' ? 'وضوح التواصل' : 'Communication clarity'}</span>
                      </li>
                      <li className="flex items-center space-x-2 rtl:space-x-reverse">
                        <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0" />
                        <span className="text-gray-700">{language === 'ar' ? 'التوافق مع الدور' : 'Role alignment'}</span>
                      </li>
                    </ul>
                    <p className="text-sm text-gray-500 bg-white/50 rounded-lg p-3">
                      {language === 'ar' ? 'تتم مراجعة مقابلات الفيديو كجزء من الملف الشامل للمرشح ولا تُستخدم كأداة قرار مستقلة.' : 'Video interviews are reviewed as part of the overall candidate profile and are not used as a standalone decision-making tool.'}
                    </p>
                  </div>
                  <div className="bg-purple-100 rounded-xl aspect-video flex items-center justify-center">
                    <Video className="h-16 w-16 text-purple-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* AI Analysis Section */}
          <div className="mb-20">
            <Card className="border-2 border-blue-200 bg-blue-50/30">
              <CardContent className="p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="order-2 md:order-1 bg-blue-100 rounded-xl aspect-video flex items-center justify-center">
                    <Brain className="h-16 w-16 text-blue-400" />
                  </div>
                  <div className="order-1 md:order-2">
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                      <Brain className="h-7 w-7 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {language === 'ar' ? 'مراجعة بمساعدة الذكاء الاصطناعي' : 'AI-Assisted Review'}
                    </h3>
                    <p className="text-gray-700 mb-4">
                      {language === 'ar' ? 'يدعم نظامنا عملية التوظيف من خلال:' : 'Our system supports the recruitment process by:'}
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center space-x-2 rtl:space-x-reverse">
                        <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        <span className="text-gray-700">{language === 'ar' ? 'هيكلة معلومات المرشح' : 'Structuring candidate information'}</span>
                      </li>
                      <li className="flex items-center space-x-2 rtl:space-x-reverse">
                        <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        <span className="text-gray-700">{language === 'ar' ? 'الإشارة إلى المستندات المفقودة' : 'Flagging missing documentation'}</span>
                      </li>
                      <li className="flex items-center space-x-2 rtl:space-x-reverse">
                        <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        <span className="text-gray-700">{language === 'ar' ? 'دعم اكتمال الملف ومطابقة الدور' : 'Supporting profile completeness and role matching'}</span>
                      </li>
                    </ul>
                    <p className="text-gray-600 mb-4">
                      {language === 'ar' ? 'تُستخدم أدوات الذكاء الاصطناعي للمساعدة في كفاءة سير العمل والاتساق.' : 'AI tools are used to assist with workflow efficiency and consistency.'}
                    </p>
                    <p className="text-sm text-gray-500 bg-white/50 rounded-lg p-3">
                      {language === 'ar' ? 'المراجعات النهائية وقرارات التحقق ونتائج التوظيف تتضمن إشرافاً بشرياً وعمليات تنظيمية.' : 'Final reviews, verification decisions, and hiring outcomes involve human oversight and regulatory processes.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Features */}
          <div className="mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              {language === 'ar' ? 'ما تحصل عليه' : 'What You Get'}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <Card key={index} className="border-2 hover:border-[#42b1c2] transition-all">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-gradient-to-br from-[#42b1c2]/10 to-[#edd982]/10 rounded-3xl p-8 md:p-12 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
              {language === 'ar' ? 'الجدول الزمني النموذجي للمرحلة المبكرة' : 'Typical Early-Stage Timeline'}
            </h2>
            <p className="text-center text-gray-500 mb-8">{language === 'ar' ? '(إرشادي فقط)' : '(Indicative Only)'}</p>
            <div className="grid md:grid-cols-3 gap-8 text-center mb-8">
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#42b1c2] mb-2">{language === 'ar' ? '1-2 يوم' : '1–2 days'}</div>
                <div className="text-gray-700 font-medium mb-2">{language === 'ar' ? 'إعداد الملف والفحوصات الأولية' : 'Profile Setup & Initial Checks'}</div>
                <p className="text-sm text-gray-500">{language === 'ar' ? 'بمجرد تقديم المعلومات المطلوبة' : 'Once required information is submitted'}</p>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#42b1c2] mb-2">{language === 'ar' ? '1-2 أسبوع' : '1–2 weeks'}</div>
                <div className="text-gray-700 font-medium mb-2">{language === 'ar' ? 'المطابقة وتنسيق المقابلات' : 'Matching & Interview Coordination'}</div>
                <p className="text-sm text-gray-500">{language === 'ar' ? 'حسب توفر صاحب العمل' : 'Subject to employer availability'}</p>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#42b1c2] mb-2">{language === 'ar' ? '2-4 أسابيع' : '2–4 weeks'}</div>
                <div className="text-gray-700 font-medium mb-2">{language === 'ar' ? 'التقدم نحو العرض والتحضير للنقل' : 'Offer Progression & Relocation Prep'}</div>
                <p className="text-sm text-gray-500">{language === 'ar' ? 'الجداول الزمنية الفعلية للنقل تختلف بشكل كبير' : 'Actual relocation timelines vary significantly'}</p>
              </div>
            </div>
          </div>

          {/* Timeline Disclaimer */}
          <div className="mb-20">
            <Card className="border border-amber-200 bg-amber-50/50">
              <CardContent className="p-4">
                <div className="flex items-start space-x-3 rtl:space-x-reverse">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-700">
                    {language === 'ar' ? 'الجداول الزمنية إرشادية فقط وقد تختلف حسب الدور وجاهزية المستندات والمتطلبات التنظيمية وعمليات صاحب العمل.' : 'Timelines are indicative only and may vary depending on role, documentation readiness, regulatory requirements, and employer processes.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* CTA */}
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {language === 'ar' ? 'هل أنت مستعد لبدء رحلتك؟' : 'Ready to Start Your Journey?'}
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              {language === 'ar' ? 'انضم إلى المتخصصين الطبيين الذين يستكشفون الفرص الوظيفية في المملكة العربية السعودية من خلال منصتنا.' : 'Join medical professionals exploring career opportunities in Saudi Arabia through our platform.'}
            </p>
            <Link to="/register/candidate">
              <Button size="lg" className="bg-[#42b1c2] hover:bg-[#3a9fb0] text-white px-8 py-6 text-lg" data-testid="candidate-cta">
                {t('howItWorksCandidates.joinTalentPool')}
                <ArrowRight className="ms-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HowItWorksCandidates;
